'use client';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { memo, useEffect, useState } from 'react';

import { AnimatedNumber } from './AnimatedNumber';

dayjs.extend(duration);

interface CountdownProps {
  targetDate: Date;
}

interface TimeLeft {
  days: number;
  hours: string;
  minutes: string;
  seconds: string;
  milliseconds: string;
}

const calculateTimeLeft = (targetDate: Date): TimeLeft => {
  const diff = dayjs(targetDate).diff(dayjs());

  if (diff <= 0) {
    return {
      days: 0,
      hours: '00',
      minutes: '00',
      seconds: '00',
      milliseconds: '00',
    };
  }

  const dur = dayjs.duration(diff);

  return {
    days: Math.floor(dur.asDays()),
    hours: String(dur.hours()).padStart(2, '0'),
    minutes: String(dur.minutes()).padStart(2, '0'),
    seconds: String(dur.seconds()).padStart(2, '0'),
    milliseconds: String(Math.floor(dur.milliseconds() / 10)).padStart(2, '0'),
  };
};

export const Countdown = memo(({ targetDate }: CountdownProps) => {
  const [mounted, setMounted] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: '00',
    minutes: '00',
    seconds: '00',
    milliseconds: '00',
  });

  useEffect(() => {
    setMounted(true);
    setTimeLeft(calculateTimeLeft(targetDate));

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 48);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (!mounted) {
    return (
      <div className="bg-Gray-white p-30 text-center">
        <time
          className="block whitespace-pre-wrap font-800 tabular-nums text-Gray-black typo-h2"
          dateTime={targetDate.toISOString()}
        >
          D-
          {'\n'}
          00:00:00:00
        </time>
      </div>
    );
  }

  return (
    <time
      className="flex flex-col items-center bg-Gray-white p-30 text-center font-800 tabular-nums text-Gray-black typo-h2"
      dateTime={targetDate.toISOString()}
    >
      <div className="flex-center">
        {timeLeft.days === 0 ? (
          <span>D-DAY</span>
        ) : (
          <>
            <span>D-</span>
            <span className="inline-block min-w-[1.5ch]">
              <AnimatedNumber value={timeLeft.days} />
            </span>
          </>
        )}
      </div>

      <div className="flex-center">
        <span className="inline-block w-[2ch] text-center">
          <AnimatedNumber value={timeLeft.hours} />
        </span>
        <span>:</span>
        <span className="inline-block w-[2ch] text-center">
          <AnimatedNumber value={timeLeft.minutes} />
        </span>
        <span>:</span>
        <span className="inline-block w-[2ch] text-center">
          <AnimatedNumber value={timeLeft.seconds} />
        </span>
        <span>:</span>
        <span className="inline-block w-[2ch] text-center">
          <AnimatedNumber value={timeLeft.milliseconds} />
        </span>
      </div>
    </time>
  );
});

Countdown.displayName = 'Countdown';
